/**
 * Owner: Masa Bayu
 * File: DedicatedMyBooksScreen.tsx
 * Date:Time: 2026-10-17 17:50
 * Description: Dedicated author book-management shelf — real Writer projection (status,
 *   local delete, QXWriter handoff, view publication). Profile palette, purpose-built UI.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { listCatalog } from '../../publication/catalog-port';
import { STUDIO_ENTRY } from '../author-production-identity';
import {
  deleteLocalMyBook,
  handoffOpenWriterBook,
  handoffOpenWriterLibrary,
  projectMyBooks,
  type MyBookCard,
} from '../../my-books/my-books-projection';
import './dedicated.css';

const COVER_TONES = ['#7c3aed', '#db2777', '#0891b2', '#15803d', '#c2410c', '#4f46e5'];

function openWriter(): void {
  if (typeof window === 'undefined' || typeof window.open !== 'function') return;
  try {
    window.open(STUDIO_ENTRY, '_self', 'noopener,noreferrer');
  } catch {
    /* restricted host */
  }
}

export interface DedicatedMyBooksScreenProps {
  readonly onNavigate: (path: string) => void;
}

export function DedicatedMyBooksScreen({ onNavigate }: DedicatedMyBooksScreenProps) {
  const [revision, setRevision] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<MyBookCard | null>(null);

  const books = useMemo(() => {
    const catalog = listCatalog().map((entry) => ({
      publicationId: entry.publicationId,
      title: entry.title,
    }));
    return projectMyBooks({ catalog });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const refresh = () => {
    setRevision((value) => value + 1);
  };

  return (
    <div className="ddx">
      <header>
        <p className="ddx__eyebrow">Author</p>
        <h2 className="ddx__title">My Books</h2>
        <p className="ddx__lede">
          The titles you write and publish on this device. Open Studio to edit, track publish
          status, or remove a local draft.
        </p>
      </header>

      <div className="ddx__toolbar">
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            handoffOpenWriterLibrary();
            openWriter();
          }}
        >
          Open Studio
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Author dashboard
        </button>
      </div>

      {books.length === 0 ? (
        <div className="ddx__empty">
          <h3>No books on this device yet</h3>
          <p>Create a book in Studio. When you publish, its status shows here.</p>
          <button
            type="button"
            className="ddx__cta ddx__cta--solid"
            onClick={() => {
              handoffOpenWriterLibrary();
              openWriter();
            }}
          >
            Open Studio
          </button>
        </div>
      ) : (
        <div className="ddx__rows">
          {books.map((book, index) => {
            const publicationId = book.publicationId;
            return (
              <article className="ddx__row" key={book.id}>
                <span
                  className="ddx__cover"
                  style={{ '--tone': COVER_TONES[index % COVER_TONES.length] } as CSSProperties}
                >
                  {book.title}
                </span>
                <div className="ddx__row-meta">
                  <h4>{book.title}</h4>
                  <p>{book.updatedLabel}</p>
                  {book.statusLabel ? (
                    <span
                      className={
                        book.statusKind === 'published'
                          ? 'ddx__pill ddx__pill--published'
                          : 'ddx__pill ddx__pill--changed'
                      }
                    >
                      {book.statusLabel}
                    </span>
                  ) : (
                    <span className="ddx__pill ddx__pill--draft">Draft</span>
                  )}
                </div>
                <div className="ddx__row-actions">
                  <button
                    type="button"
                    className="ddx__cta"
                    onClick={() => {
                      handoffOpenWriterBook(book.id);
                      openWriter();
                    }}
                  >
                    Open in Studio
                  </button>
                  {publicationId !== null ? (
                    <button
                      type="button"
                      className="ddx__cta"
                      onClick={() => {
                        onNavigate(`/publication/${publicationId}`);
                      }}
                    >
                      View publication
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ddx__cta"
                    onClick={() => {
                      setPendingDelete(book);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pendingDelete ? (
        <div
          className="ddx__dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Delete book"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setPendingDelete(null);
            }
          }}
        >
          <div>
            <h2>Delete “{pendingDelete.title}”?</h2>
            <p>Removes this book from this device. It does not unpublish a catalog listing.</p>
            <div className="ddx__dialog-actions">
              <button
                type="button"
                className="ddx__cta"
                onClick={() => {
                  setPendingDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ddx__cta ddx__cta--solid"
                onClick={() => {
                  if (deleteLocalMyBook(pendingDelete.id)) {
                    setPendingDelete(null);
                    refresh();
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
